import { useEffect, useState } from 'preact/hooks';
import { api, signInUrl, type BotLeagueSummary } from '../api';
import { DIVISIONS, DivisionTile, divisionOf } from '../divisions';
import { useMe } from '../me';
import { Link } from '../router';
import { botLeagueAddress } from './discord-league';

/** Each division's latest league, as a tile with its artwork (nothing if there are none) */
function Divisions() {
	const [leagues, setLeagues] = useState<BotLeagueSummary[]>([]);
	useEffect(() => {
		api.get<{ leagues: BotLeagueSummary[] }>('/api/bot-leagues').then(result => setLeagues(result.leagues), () => setLeagues([]));
	}, []);
	const tiles = DIVISIONS.flatMap(division => {
		const league = leagues.filter(l => l.status !== 'cancelled' && divisionOf(l) === division).sort((a, b) => b.id - a.id)[0];
		return league ? [{ division, league }] : [];
	});
	if (!tiles.length) return null;
	return <section class="division-tiles" aria-label="Divisions">
		{tiles.map(({ division, league }) => <DivisionTile key={division.id} division={division} href={botLeagueAddress(league)}
			note={`${league.pools.reduce((n, p) => n + p.players, 0)} coaches`} />)}
	</section>;
}

export function HomePage() {
	const me = useMe();
	const user = me?.user;
	return <>
		<div class="card hero">
			<img src="/brand/logo-256.png" alt="OgreClan" />
			<div>
				<h1>OgreClan draft leagues</h1>
				<p>Draft your team, trade, and follow your league here or on <a href="https://play.ogreclan.com">play.ogreclan.com</a>:
					it's the same league in both places, so a pick made here shows up in Showdown's draft room straight away.</p>
				{!me ? null : !user ? <p>
					<a class="button primary" href={signInUrl('/league')}>Sign in with Discord</a>{' '}
					<span class="muted">Anyone can look at public leagues without signing in.</span>
				</p> : !user.showdown ? <p class="notice">
					Welcome, {user.name}! To play in your leagues here, <Link href="/account">link your Showdown name</Link>: it takes one
					command on Showdown.
				</p> : <p>Signed in as <strong>{user.showdown.name}</strong>. <Link href="/league">Go to your leagues</Link>.</p>}
			</div>
		</div>
		<Divisions />
		<div class="grid">
			<div class="card">
				<h2>Leagues</h2>
				<p>Rosters, the pool and its points, the schedule, results and standings. When the draft is on, make your picks
					here, with the same timer as Showdown.</p>
				<Link class="button" href="/league">See the leagues</Link>
			</div>
			<div class="card">
				<h2>Draft Planner</h2>
				<p>Type charts, coverage, speed tiers and move search for any team, with as many saved drafts and matchups as you
					like. They're the same ones as <code>/planner</code> on Showdown.</p>
				<Link class="button" href="/plan">Open the planner</Link>
			</div>
			<div class="card">
				<h2>Guides</h2>
				<p>How everything works: the site, the Discord bot, the league spreadsheet, play.ogreclan.com and replays.</p>
				<Link class="button" href="/guides">Read the guides</Link>
			</div>
			<div class="card">
				<h2>Replays</h2>
				<p>Battles saved on play.ogreclan.com, to watch again or share.</p>
				<a class="button" href="/replays/">Watch replays</a>
			</div>
		</div>
	</>;
}
